import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { motion, useAnimationControls, useMotionValue, useTransform } from "framer-motion";
import { Info, Star } from "lucide-react";
import { posterUrl } from "@/lib/api";

export const DIRS = {
  right: { label: "WANT TO WATCH", color: "hsl(var(--sp-right))", exit: { x: 700, y: 40 } },
  left: { label: "PASS", color: "hsl(var(--sp-left))", exit: { x: -700, y: 40 } },
  up: { label: "LIKED IT", color: "hsl(var(--sp-up))", exit: { x: 0, y: -900 } },
  down: { label: "DIDN'T LIKE", color: "hsl(var(--sp-down))", exit: { x: 0, y: 900 } },
};

const Stamp = ({ dir, opacity, className }) => (
  <motion.div style={{ opacity, borderColor: DIRS[dir].color, color: DIRS[dir].color }} data-testid={`swipe-stamp-${dir}`}
    className={`absolute z-40 px-4 py-2 border-4 rounded-xl font-display font-black text-2xl tracking-wider bg-black/30 backdrop-blur-sm pointer-events-none ${className}`}>
    {DIRS[dir].label}
  </motion.div>
);

const EXIT_VARIANTS = {
  out: (dir) => ({ ...(DIRS[dir]?.exit || DIRS.left.exit), opacity: 0, zIndex: 50, pointerEvents: "none",
    transition: { duration: 0.24, ease: "easeIn", opacity: { duration: 0.14 } } }),
};

function pickDirection({ offset, velocity }) {
  const ax = Math.abs(offset.x), ay = Math.abs(offset.y);
  if (ax > ay && (ax > 110 || Math.abs(velocity.x) > 700)) return offset.x > 0 ? "right" : "left";
  if (ay >= ax && (ay > 110 || Math.abs(velocity.y) > 700)) return offset.y > 0 ? "down" : "up";
  return null;
}

export const SwipeCard = forwardRef(function SwipeCard({ movie, onSwipe, onDetails, allowed = ["right", "left", "up", "down"], draggable = true, exitInParent = false }, ref) {
  const tapStart = useRef(null);
  const swiped = useRef(false);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotate = useTransform(x, [-300, 300], [-14, 14]);
  const op = {
    right: useTransform(x, [30, 120], [0, 1]),
    left: useTransform(x, [-120, -30], [1, 0]),
    up: useTransform(y, [-120, -30], [1, 0]),
    down: useTransform(y, [30, 120], [0, 1]),
  };
  const controls = useAnimationControls();

  const fly = async (dir) => {
    // Each card can only be swiped once; later input belongs to the next card.
    if (!allowed.includes(dir) || swiped.current) return;
    swiped.current = true;
    if (exitInParent) return onSwipe(dir);
    await controls.start({ ...DIRS[dir].exit, opacity: 0, transition: { duration: 0.32, ease: "easeIn" } });
    onSwipe(dir);
  };
  useImperativeHandle(ref, () => ({ fly }));
  useEffect(() => { controls.start({ scale: 1, transition: { duration: 0.15 } }); }, [controls]);

  const onDragEnd = (_, info) => {
    const dir = pickDirection(info);
    if (dir && allowed.includes(dir)) fly(dir);
    else if (!swiped.current) controls.start({ x: 0, y: 0, transition: { type: "spring", stiffness: 320, damping: 24 } });
  };

  const onPointerDown = (e) => { tapStart.current = { x: e.clientX, y: e.clientY, t: Date.now() }; };
  const onPointerUp = (e) => {
    const s = tapStart.current;
    tapStart.current = null;
    if (s && onDetails && Math.hypot(e.clientX - s.x, e.clientY - s.y) < 6 && Date.now() - s.t < 450) onDetails(movie);
  };

  return (
    <motion.div data-testid="movie-swipe-card" data-movie-id={movie.movie_id} drag={draggable} dragElastic={0.9} onDragEnd={onDragEnd} animate={controls}
      onPointerDown={onPointerDown} onPointerUp={onPointerUp} variants={EXIT_VARIANTS} exit="out"
      style={{ x, y, rotate }} initial={{ scale: 0.97, opacity: 1 }}
      className="absolute inset-0 z-30 rounded-3xl overflow-hidden border border-white/10 shadow-2xl bg-card cursor-grab active:cursor-grabbing touch-none select-none">
      {movie.poster_path ? (
        <img src={posterUrl(movie.poster_path, "w780")} alt={movie.title} draggable={false} className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <div className="absolute inset-0 bg-secondary flex items-center justify-center text-muted-foreground font-display font-bold text-lg px-8 text-center">No poster available</div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
      {onDetails && (
        <button type="button" aria-label="Movie details" data-testid="movie-card-info-button"
          onPointerDown={(e) => e.stopPropagation()} onPointerUp={(e) => e.stopPropagation()} onClick={() => onDetails(movie)}
          className="absolute top-4 right-4 z-40 h-10 w-10 rounded-full bg-black/50 backdrop-blur text-white flex items-center justify-center transition-transform hover:scale-110">
          <Info className="h-5 w-5" />
        </button>
      )}
      {allowed.includes("right") && <Stamp dir="right" opacity={op.right} className="top-8 left-6 -rotate-12" />}
      {allowed.includes("left") && <Stamp dir="left" opacity={op.left} className="top-8 right-6 rotate-12" />}
      {allowed.includes("up") && <Stamp dir="up" opacity={op.up} className="bottom-40 left-1/2 -translate-x-1/2" />}
      {allowed.includes("down") && <Stamp dir="down" opacity={op.down} className="top-10 left-1/2 -translate-x-1/2" />}
      <CardInfo movie={movie} />
    </motion.div>
  );
});

export const CardInfo = ({ movie }) => (
  <div className="absolute bottom-0 inset-x-0 p-6 text-white">
    <div className="flex items-center gap-2 text-xs font-semibold text-white/70 mb-2">
      {movie.vote_average > 0 && (
        <span className="flex items-center gap-1"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />{movie.vote_average.toFixed(1)}</span>
      )}
      {movie.genres?.slice(0, 3).map((g) => <span key={g} className="px-2 py-0.5 rounded-full bg-white/10 backdrop-blur">{g}</span>)}
    </div>
    <h2 className="text-3xl font-black leading-tight" data-testid="movie-card-title">{movie.title}</h2>
    <p className="text-white/60 font-semibold" data-testid="movie-card-year">{movie.year || "—"}</p>
    {movie.overview && <p className="text-sm text-white/70 mt-2 line-clamp-2">{movie.overview}</p>}
    <p className="text-[11px] text-white/45 mt-2 uppercase tracking-widest">Tap for cast & trailer</p>
  </div>
);

export const CardBehind = ({ movie }) => (
  <div className="absolute inset-0 z-20 rounded-3xl overflow-hidden border border-white/10 scale-[0.94] translate-y-4 opacity-60 bg-card">
    {movie && <img src={posterUrl(movie.poster_path, "w780")} alt="" className="h-full w-full object-cover" />}
  </div>
);
